// src/pages/driver/DriverSessionsPage.js
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Box, 
  Typography, 
  Button, 
  Grid, 
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  styled
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import BoltIcon from '@mui/icons-material/Bolt';
import { getSessions } from '../../api/sessionAPI';
import DriverSessionList from '../../components/session/DriverSessionList';
import LoadingSpinner from '../../components/common/Loadingspinner';
import ErrorAlert from '../../components/common/ErrorAlert';

// Styled components for consistent sizing
const StyledFormControl = styled(FormControl)(({ theme }) => ({
  minHeight: 80, // Ensure enough height for the label and content
  width: '100%',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  minHeight: 80, // Match the height of the FormControl
  width: '100%',
}));

// Fixed Select component with proper padding
const StyledSelect = styled(Select)(({ theme }) => ({
  width: '100%',
  '& .MuiSelect-select': {
    paddingRight: '32px !important', // Ensure space for dropdown icon
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  }
}));

const DriverSessionsPage = () => {
  const [filters, setFilters] = useState({
    status: '',
    startDate: null,
    endDate: null
  });

  // Fetch sessions with filters - Driver only sees their own sessions (backend handles filtering)
  const { data: sessions, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['driver-sessions', filters],
    queryFn: () => getSessions({
      // Driver-specific filters - no company/site/charger as driver only sees their own
      status: filters.status || undefined,
      start_date: filters.startDate ? filters.startDate.toISOString() : undefined,
      end_date: filters.endDate ? filters.endDate.toISOString() : undefined
    })
  });

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleResetFilters = () => {
    setFilters({
      status: '',
      startDate: null,
      endDate: null
    });
  };

  // Menu props that match the width of the select without being oversized
  const menuProps = {
    PaperProps: {
      style: {
        maxHeight: 300,
      },
    },
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left',
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'left',
    },
    MenuListProps: {
      disableAutoFocusItem: true,
      style: { paddingTop: 0, paddingBottom: 0 }
    },
  };

  // Common style for input fields to ensure consistency
  const inputStyle = {
    height: 56, // Standard Material UI input height
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError) {
    return <ErrorAlert message={error instanceof Error ? error.message : 'Error loading sessions'} />;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <BoltIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4">My Charging Sessions</Typography>
        </Box>
        <Button 
          variant="outlined" 
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
        >
          Refresh
        </Button>
      </Box>

      {/* Simplified Filters Panel for Drivers */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <FilterListIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Filter My Sessions</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={handleResetFilters} size="small">
            Reset
          </Button>
        </Box>
        
        <Grid container spacing={3}>
          {/* Single Row of Filters - Simplified for Drivers */}
          <Grid item xs={12}>
            <Grid container spacing={3}>
              {/* Status Filter */}
              <Grid item xs={12} sm={6} md={4}>
                <StyledFormControl>
                  <InputLabel id="status-filter-label" shrink>Status</InputLabel>
                  <StyledSelect
                    labelId="status-filter-label"
                    name="status"
                    value={filters.status}
                    label="Status"
                    onChange={handleFilterChange}
                    MenuProps={menuProps}
                    sx={inputStyle}
                    displayEmpty
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    <MenuItem value="Started">Active</MenuItem>
                    <MenuItem value="Completed">Completed</MenuItem>
                    <MenuItem value="Stopped">Stopped</MenuItem>
                    <MenuItem value="Faulted">Faulted</MenuItem>
                  </StyledSelect>
                </StyledFormControl>
              </Grid>

              {/* Start Date Filter */}
              <Grid item xs={12} sm={6} md={4}>
                <StyledTextField
                  label="From Date"
                  type="datetime-local"
                  value={filters.startDate ? filters.startDate.toISOString().slice(0, 16) : ''}
                  onChange={(e) => handleDateChange('startDate', e.target.value ? new Date(e.target.value) : null)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarMonthIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    style: inputStyle
                  }}
                  fullWidth
                />
              </Grid>

              {/* End Date Filter */}
              <Grid item xs={12} sm={6} md={4}>
                <StyledTextField
                  label="To Date"
                  type="datetime-local"
                  value={filters.endDate ? filters.endDate.toISOString().slice(0, 16) : ''}
                  onChange={(e) => handleDateChange('endDate', e.target.value ? new Date(e.target.value) : null)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarMonthIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    style: inputStyle
                  }}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* Sessions List - Using Driver-specific component */}
      <Box>
        <DriverSessionList 
          sessions={sessions}
          isLoading={isLoading}
          isError={isError}
          error={error}
        />
      </Box>
    </Box>
  );
};

export default DriverSessionsPage;